/* eslint-disable @typescript-eslint/no-explicit-any */
import DataTable from "@/components/data-table";
import { ColumnVisibility } from "@/components/data-table/column-visiblity";
import InitialPageVisitGuideModal from "@/components/page-visit-guide";
import { AddDomainDialog } from "@/components/popups/add-domain";
import { FilterLabelsButton } from "@/components/ui/filter-labels-button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SearchInput } from "@/components/ui/search-input";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useLabels } from "@/hooks/use-labels";
import {
  useAddLabelsToPostmasterDomain,
  useGetDomainsWithLatestData,
  useGetStats,
  useRemoveLabelsFromPostmasterDomains,
  useUpdateDomainsSyncStatus,
} from "@/hooks/use-postmaster";
import { usePermission } from "@/hooks/use-premission";
import { cn } from "@/lib/utils";
import ManageLabels from "@/pages/labels/manage-label";
import { useServices } from "@/services/hooks/useServices";
import { LabelType } from "@/types/common";
import { PostmasterDomain } from "@/types/postman.types";
import { Tag as TagIcon } from "@phosphor-icons/react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { type ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import {
  AlertCircle,
  Check,
  CheckCircle2,
  ChevronDown,
  Download,
  EllipsisVertical,
  Globe,
  Plus,
  Shield,
  Tag,
  XCircle,
} from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import Trash from "@/components/icons/trash";

export const Route = createFileRoute("/_private/postmaster/")({
  component: RouteComponent,
});
const LabelCellRenderer = (params: any) => {
  const [isOpen, setIsOpen] = useState(false);
  const [updatingLabelId, setUpdatingLabelId] = useState<string | null>(null);
  const { mutateAsync: addLabelsToPostmasterDomain, isPending: isAdding } =
    useAddLabelsToPostmasterDomain();
  const {
    mutateAsync: removeLabelsFromPostmasterDomains,
    isPending: isUpdating,
  } = useRemoveLabelsFromPostmasterDomains();
  const { data: labels } = useLabels(1, 100, LabelType.POSTMASTER);
  const { hasPermission } = usePermission();

  const renderLabels = () => {
    // Check if labels exist and handle accordingly
    if (!params.data.labels || params.data.labels.length === 0) {
      return <span className="text-xs text-muted-foreground">No labels</span>;
    }

    // Get the first label if it exists
    const firstLabel = params.data.labels[0];

    return (
      <>
        {firstLabel && (
          <Badge
            key={firstLabel._id}
            style={{ backgroundColor: firstLabel.color }}
            className="text-background text-xs py-0.5 px-1.5 shadow-none rounded-sm"
          >
            {firstLabel.name}
          </Badge>
        )}
        {params.data.labels.length > 1 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge className="text-accent-foreground bg-accent text-xs py-0.5 px-1.5 shadow-none rounded-sm">
                +{params.data.labels.length - 1}
              </Badge>
            </TooltipTrigger>
            <TooltipContent className="bg-black text-white">
              <div className="flex flex-col gap-1">
                {params.data.labels.slice(1).map((label: any) => (
                  <div key={label._id} className="flex items-center gap-2">
                    <div
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: label?.color }}
                    />
                    <span className="text-xs">{label?.name}</span>
                  </div>
                ))}
              </div>
            </TooltipContent>
          </Tooltip>
        )}
      </>
    );
  };

  if (!hasPermission("postmaster:edit")) {
    return <div className="h-full flex items-center">{renderLabels()}</div>;
  }

  const combineLoading = isUpdating || isAdding;

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <div className="inline-flex w-max min-w-40  justify-between h-8 px-2 rounded-md border border-input hover:bg-accent hover:text-accent-foreground transition-colors">
          <div className="flex items-center gap-1 flex-1 justify-start w-4/5">
            {renderLabels()}
          </div>
          <div className="flex items-center gap-1 justify-center w-1/5">
            <ChevronDown className="h-4 ml-1 opacity-50" />
          </div>
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[200px]">
        {labels?.data?.map((label) => (
          <DropdownMenuItem
            key={label._id}
            className="flex items-center gap-2 py-1.5"
            disabled={combineLoading && updatingLabelId === label._id}
            onSelect={(e) => {
              e.preventDefault();
              const hasLabel = params.data.labels?.some(
                (l: any) => l._id === label._id
              );
              setUpdatingLabelId(label._id);

              if (hasLabel) {
                removeLabelsFromPostmasterDomains(
                  {
                    domains: [params.data.domain],
                    labelIds: [label._id],
                  },
                  {
                    onSettled: () => setUpdatingLabelId(null),
                  }
                );
              } else {
                addLabelsToPostmasterDomain(
                  {
                    domain: [params.data.domain],
                    labelIds: [label._id],
                  },
                  {
                    onSettled: () => setUpdatingLabelId(null),
                  }
                );
              }
            }}
          >
            <div className="flex items-center gap-2 flex-1">
              <TagIcon weight="fill" size={16} style={{ fill: label.color }} />
              <span className="text-sm">{label.name}</span>
            </div>
            {updatingLabelId === label._id && combineLoading ? (
              <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            ) : (
              params.data.labels?.some?.((l: any) => l._id === label._id) && (
                <Check className="h-3.5 w-3.5" />
              )
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const getStatusConfig = (status: string) => {
  switch (status) {
    case "Excellent":
      return {
        color:
          "bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400",
        icon: <CheckCircle2 className="h-4 w-4" />,
      };
    case "Good":
      return {
        color:
          "bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400",
        icon: <CheckCircle2 className="h-4 w-4" />,
      };
    case "Fair":
      return {
        color:
          "bg-yellow-50 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400",
        icon: <AlertCircle className="h-4 w-4" />,
      };
    case "Needs Attention":
    default:
      return {
        color: "bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400",
        icon: <XCircle className="h-4 w-4" />,
      };
  }
};

const getReputationClass = (value: string) => {
  switch (value) {
    case "high":
      return "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400 ";
    case "medium":
      return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400  ";
    case "low":
      return "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400 ";
    default:
      return "bg-slate-100 text-slate-800 dark:bg-slate-900/20 dark:text-slate-400 ";
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case "Good":
      return "text-green-600";
    case "Fair":
      return "text-yellow-600";
    case "Poor":
      return "text-orange-600";
    case "Needs Attention":
    default:
      return "text-red-600";
  }
};

function RouteComponent() {
  const navigate = useNavigate();
  const [showManageLabels, setShowManageLabels] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(100);
  const [search, setSearch] = useState("");
  const [selectedLabels, setSelectedLabels] = useState<string[]>([]);

  const { data: labels } = useLabels(1, 100, LabelType.POSTMASTER);
  const { data: domains, isLoading: isDomainsLoading } =
    useGetDomainsWithLatestData({
      filters: {
        page,
        limit,
        search,
        isSynced: true,
        labels: selectedLabels.length > 0 ? selectedLabels : undefined,
      },
    });
  const { postmasterService } = useServices();
  const [isOpen, setIsOpen] = useState(false);
  const { mutate: updateDomainsSyncStatus } = useUpdateDomainsSyncStatus();
  const { data: stats } = useGetStats();
  const [domainToDelete, setDomainToDelete] = useState<string | null>(null);
  const gridRef = useRef<AgGridReact>(null);
  const [selectedRows, setSelectedRows] = useState<PostmasterDomain[]>([]);
  const { hasPermission } = usePermission();
  const handleRowClick = (event: any) => {
    // Get the clicked cell's column from the event
    const column = event.api.getFocusedCell()?.column;
    if (column?.getColId() === "actions") {
      return;
    }

    navigate({
      to: `/postmaster/${event.data.domain}`,
    });
  };

  const handleExport = async () => {
    try {
      const response = await postmasterService.exportDomainsData();

      // Create blob from response
      const blob = new Blob([response as unknown as BlobPart], {
        type: "text/csv",
      });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "postmaster-domains.csv";

      // Trigger download
      document.body.appendChild(a);
      a.click();

      // Cleanup
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      window.cioanalytics?.track("postmaster_domains_exported", {});
    } catch (error) {
      console.error("Export failed:", error);
      // Add your error handling here
    }
  };

  const handleAddDomains = (selectedDomains: string[]) => {
    // console.log(selectedDomains, "selected domains");
    updateDomainsSyncStatus(
      { domains: selectedDomains, isSynced: true },
      {
        onSuccess: () => {
          toast.success("Domains added successfully");
          window.cioanalytics?.track("postmaster_domains_added", {
            count: selectedDomains.length,
            domains: selectedDomains,
          });
          setIsOpen(false);
        },
        onError: () => {
          toast.error("Failed to add domains");
        },
      }
    );
    // Implement your domain addition logic here
    // setIsOpen(false);
  };

  const columnDefs: ColDef<PostmasterDomain>[] = [
    {
      field: "domain",
      headerName: "Domain",
      filter: true,
      sortable: true,
      minWidth: 200,
      flex: 1,
    },
    {
      field: "labelIds",
      headerName: "Labels",
      sortable: false,
      filter: false,
      minWidth: 250,
      flex: 1,
      cellRenderer: LabelCellRenderer,
    },
    {
      field: "gpmStatus",
      headerName: "Google Postmaster",
      filter: true,
      sortable: true,
      minWidth: 180,
      flex: 1,
      cellRenderer: (params: any) => {
        const isConnected = params.value === "GPM Connected";
        return (
          <div className="flex items-center gap-2 h-full">
            <Badge
              variant="secondary"
              className={cn(
                "flex items-center gap-2",
                isConnected
                  ? "bg-green-500/10 text-green-500 hover:bg-green-500/10"
                  : "bg-slate-500/10 text-slate-400 hover:bg-slate-500/10"
              )}
            >
              <span
                className={cn(
                  "size-2 rounded-full",
                  isConnected ? "bg-green-500" : "bg-slate-400"
                )}
              />
              <span className="text-xs">{params.value}</span>
            </Badge>
          </div>
        );
      },
    },
    {
      field: "reputation",
      headerName: "Domain Reputation",
      filter: true,
      sortable: true,
      minWidth: 180,
      flex: 1,
      cellRenderer: (params: any) => {
        const value = params.value?.toLowerCase();

        return (
          <span
            className={cn(
              "px-2 py-1 rounded-full text-xs font-medium capitalize",
              getReputationClass(value)
            )}
          >
            {params.value === "UNKNOWN" ? "Unknown" : params.value}
          </span>
        );
      },
    },
    {
      field: "ipReputation",
      headerName: "IP Reputation",
      filter: true,
      sortable: true,
      minWidth: 180,
      flex: 1,
      valueGetter: (params: any) =>
        params.data?.ipReputation?.status ?? "Needs Attention",
      cellRenderer: (params: any) => {
        const status = params.value;

        const config = getStatusConfig(status);
        return (
          <div className="flex items-center h-full w-full">
            <Badge
              variant="outline"
              className={cn(
                "flex items-center gap-2 border-none",
                config.color
              )}
            >
              {config.icon}
              <span>{status}</span>
            </Badge>
          </div>
        );
      },
    },
    {
      field: "spamRate",
      headerName: "Spam Rate",
      filter: true,
      sortable: true,
      minWidth: 180,
      flex: 1,
      valueFormatter: (params) => `${params.value}%`,
    },
    {
      field: "authentication",
      headerName: "Authentication",
      filter: true,
      sortable: true,
      minWidth: 250,
      flex: 1,
      cellRenderer: (params: any) => {
        const auth = params.value;

        return (
          <div className="flex items-center justify-between">
            <div className="flex flex-col items-center gap-1">
              <span className="text-xs text-muted-foreground">DKIM</span>
              <span
                className={`text-xs font-medium ${getStatusColor(
                  auth.dkim.status
                )}`}
              >
                {auth.dkim.current}%
              </span>
            </div>

            <div className="flex flex-col items-center gap-1">
              <span className="text-xs text-muted-foreground">DMARC</span>
              <span
                className={`text-xs font-medium ${getStatusColor(
                  auth.dmarc.status
                )}`}
              >
                {auth.dmarc.current}%
              </span>
            </div>

            <div className="flex flex-col items-center gap-1">
              <span className="text-xs text-muted-foreground">SPF</span>
              <span
                className={`text-xs font-medium ${getStatusColor(
                  auth.spf.status
                )}`}
              >
                {auth.spf.current}%
              </span>
            </div>
          </div>
        );
      },
    },
    {
      field: "inboundEncryptionRatio",
      headerName: "Inbound Encryption Ratio",
      filter: true,
      sortable: true,
      minWidth: 180,
      flex: 1,
      valueFormatter: (params) => `${params.value}%`,
    },
    {
      field: "outboundEncryptionRatio",
      headerName: "Outbound Encryption Ratio",
      filter: true,
      sortable: true,
      minWidth: 180,
      flex: 1,
      valueFormatter: (params) => `${params.value}%`,
    },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      filter: false,
      minWidth: 100,
      resizable: false,
      cellRenderer: (params: any) => {
        // console.log(params, "params");
        return hasPermission("postmaster:delete") ? (
          <div
            className="flex items-center gap-2 h-full justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 group delete-button"
              onClick={(e) => {
                e.stopPropagation();
                setDomainToDelete(params.data.domain);
              }}
            >
              <Trash className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <></>
        );
      },
    },
  ];
  const onSelectionChanged = () => {
    const selectedNodes = gridRef?.current?.api.getSelectedNodes();
    const selectedData = selectedNodes?.map((node) => node.data) || [];
    setSelectedRows(selectedData);
  };

  const handleLabelsChange = (newLabels: string[]) => {
    setSelectedLabels(newLabels);
  };

  const statsCards = useMemo(() => {
    return [
      {
        key: "totalsDomains",
        title: "Total Domains",
        value: stats?.totalDomains?.count || 0,
        icon: <Globe className="w-5 h-5 text-blue-500 dark:text-blue-400" />,
        cardClass:
          "bg-gradient-to-br from-blue-50 dark:from-blue-900/20 to-blue-100 dark:to-blue-900/20 border-blue-200 dark:border-blue-400",
      },
      {
        key: "highReputation",
        title: "High Reputation",
        value: stats?.highReputation?.count || 0,
        icon: <Shield className="w-5 h-5 text-green-500 dark:text-green-400" />,
        cardClass:
          "bg-gradient-to-br from-green-50 dark:from-green-900/20 to-green-100 dark:to-green-900/20 border-green-200 dark:border-green-400",
      },
      {
        key: "goodSpamRate",
        title: "Good Spam Rate",
        value: stats?.goodSpamRate?.count || 0,
        icon: <Shield className="w-5 h-5 text-blue-500 dark:text-blue-400" />,
        cardClass:
          "bg-gradient-to-br from-blue-50 dark:from-blue-900/20 to-blue-100 dark:to-blue-900/20 border-blue-200 dark:border-blue-400",
      },
      {
        key: "needsAttention",
        title: "Needs Attention",
        value: stats?.totalDomains?.count || 0,
        icon: (
          <AlertCircle className="w-5 h-5 text-red-500 dark:text-red-400" />
        ),
        cardClass:
          "bg-gradient-to-br from-red-50 dark:from-red-900/20 to-red-100 dark:to-red-900/20 border-red-200 dark:border-red-400",
      },
    ];
  }, [stats]);

  return (
    <>
      <div className="flex flex-col gap-4 w-full">
        {/* Header */}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <h1 className="page-heading">Postmaster</h1>
          </div>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center">
            <Button variant="outline" onClick={handleExport}>
              <Download className="h-4 w-4" />
              Export
            </Button>
            {hasPermission("postmaster:add") && (
              <Button onClick={() => setIsOpen(true)}>
                <Plus className="h-4 w-4" />
                Add domain
              </Button>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid gap-4 mb-4 grid-cols-1 sm:grid-cols-2  lg:grid-cols-4">
          {statsCards.map((card) => (
            <Card key={card.key} className={cn("shadow-none", card.cardClass)}>
              <CardHeader className="p-4 pb-2">
                <CardTitle className="text-sm text-muted-foreground font-normal">
                  {card.title}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <div className="flex items-center gap-2 text-lg font-semibold">
                  {card.icon}
                  {card.value}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="flex flex-col gap-2 lg:flex-row lg:justify-between lg:items-center">
          <SearchInput
            onSearch={(searchTerm) => {
              setSearch(searchTerm);
              if (searchTerm.length > 0) {
                setPage(1); // Reset to first page on search
              }
            }}
            placeholder="Search by domains..."
            className="max-w-none sm:w-full md:max-w-[300px]"
          />

          <div className="flex gap-2 w-full flex-row items-center justify-between flex-wrap md:flex-nowrap">
            <div className="flex gap-2">
              {/* Empty left side for now */}
            </div>

            <div className="flex items-center gap-2">
              <FilterLabelsButton
                onLabelsChange={handleLabelsChange}
                labels={
                  labels?.data?.map((label) => ({
                    value: label._id,
                    label: label.name,
                    bgcolor: label.color || "",
                  })) || []
                }
                selectedLabels={selectedLabels}
                placeholder="Filter by labels"
              />
              {selectedRows.length > 0 &&
                (hasPermission("postmaster:delete") ||
                  hasPermission("postmaster:edit")) && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline">
                        <EllipsisVertical className="h-4 w-4" />
                        <span className="hidden xs:block">Actions</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {hasPermission("postmaster:edit") && (
                        <DropdownMenuItem
                          onSelect={() => setShowManageLabels(true)}
                        >
                          <Tag className="h-4 w-4" />
                          Manage Labels
                        </DropdownMenuItem>
                      )}
                      {hasPermission("postmaster:delete") && (
                        <DropdownMenuItem
                          onSelect={() => setDomainToDelete("bulk")}
                          className="delete-button group"
                        >
                          <Trash className="h-4 w-4" />
                          Delete Selected ({selectedRows.length})
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              <ColumnVisibility
                gridRef={gridRef}
                storageKey="postmaster-columns-state"
                triggerClassName="[&>span]:hidden sm:[&>span]:block"
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <DataTable
          onRowClicked={handleRowClick}
          rowData={domains?.domains || []}
          columnDefs={columnDefs}
          onSelectionChanged={onSelectionChanged}
          ref={gridRef}
          height={550}
          loading={isDomainsLoading}
          className="w-full"
          showPagination={true}
          paginationProps={{
            pageSize: 10,
            totalPages: Math.ceil((domains?.pagination.total || 0) / limit),
            currentPage: page,
            rowsPerPage: limit,
            onRowsPerPageChange: (limit: number) => {
              setLimit(limit);
            },
            onPageChange: (page: number) => {
              setPage(page);
            },
            totalEntries: domains?.pagination.total || 0,
          }}
        />
      </div>

      <AddDomainDialog
        open={isOpen}
        onOpenChange={setIsOpen}
        onSubmit={handleAddDomains}
      />

      <AlertDialog
        open={!!domainToDelete}
        onOpenChange={() => setDomainToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
            <AlertDialogDescription>
              {domainToDelete === "bulk"
                ? `This will remove ${selectedRows.length} domains from your monitored domains list.`
                : `This will remove ${domainToDelete} from your monitored domains list.`}{" "}
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-500 hover:bg-red-600"
              onClick={() => {
                const domainsToDelete =
                  domainToDelete === "bulk"
                    ? selectedRows.map((row) => row.domain)
                    : [domainToDelete!];

                updateDomainsSyncStatus(
                  { domains: domainsToDelete, isSynced: false },
                  {
                    onSuccess: () => {
                      toast.success(
                        domainToDelete === "bulk"
                          ? "Selected domains removed successfully"
                          : "Domain removed successfully"
                      );

                      if (domainToDelete === "bulk") {
                        window.cioanalytics?.track(
                          "postmaster_domains_bulk_deleted",
                          {
                            count: domainsToDelete.length,
                            domains: domainsToDelete,
                          }
                        );
                      } else {
                        window.cioanalytics?.track(
                          "postmaster_domains_deleted",
                          {
                            domain: domainToDelete,
                          }
                        );
                      }

                      setDomainToDelete(null);
                      setSelectedRows([]);
                    },
                    onError: () => {
                      toast.error("Failed to remove domain(s)");
                      setDomainToDelete(null);
                    },
                  }
                );
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* <ManageLabelsPopup
        open={showManageLabels}
        onOpenChange={setShowManageLabels}
        selectedMailboxIds={selectedRows.map((row) => row.domain)}
        lableType={LabelType.POSTMASTER}
      /> */}

      {showManageLabels && (
        <ManageLabels
          open={showManageLabels}
          onOpenChange={setShowManageLabels}
          selectedRows={selectedRows.map((row) => ({
            _id: row.domain,
            labels: row.labelIds,
          }))}
          labelType={LabelType.POSTMASTER}
          afterChange={() => {
            gridRef.current?.api.deselectAll();
            setSelectedRows([]);
            setShowManageLabels(false);
          }}
        />
      )}

      <InitialPageVisitGuideModal type="postmaster" />
    </>
  );
}
